"use client";

import {
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  useState,
} from "react";
import {
  Card,
  Container,
  type ContainerProps,
  Flex,
  GradientText,
  Grid,
  Heading,
  Icon,
  IconButton,
  Separate,
  Spacing,
  Suggestion,
  Text,
  TextField,
  type UserPromptRecord,
} from "@huub-web/ui";
import { mergeClasses, padNumber } from "@huub-web/utils";

import type {
  AnalysisObservation as AnalysisObservationType,
  AnalysisObservationCategory,
} from "../../types";
import type { IllustrationName } from "../../types/IllustrationType";
import { AnalysisIndicator } from "../AnalysisIndicator";
import { useContentMenuSection } from "../../hooks";
import { Section } from "../Section";
import { GaugeCard } from "../GaugeCard";
import { Pentaptych } from "../Pentaptych";
import { AnalysisObservation } from "./components/AnalysisObservation";
import { AllAnalysisObservations } from "./components/AllAnalysisObservations";
import { SummaryLoading } from "../Analysis/components/SummaryLoading";
import type { AnalysisSectionIndex } from "../Analysis/types/AnalysisSections";

import {
  plusHeadingStyle,
  plusPointsListStyle,
  plusPointsOffsetStyle,
} from "./AnalysisObservationsSection.css";

export type AnalysisObservationsSectionProps = Omit<
  AnalysisObservationCategory,
  "id"
> & {
  index: AnalysisSectionIndex;
  illustration: IllustrationName;
  container?: ContainerProps["size"];
  variant?: "default" | "pentaptych";
  main?: ReactElement<any> | false;
  lockPrompts?: boolean;
  mainImage?: string;
  loading?: boolean;
  onPrompt: (question: UserPromptRecord) => void;
  onObservationsClick: (
    index: AnalysisSectionIndex,
    observation?: AnalysisObservationType,
  ) => void;
  loadingObservations?: string[];
  noData?: boolean;
};

export const AnalysisObservationsSection: FC<
  AnalysisObservationsSectionProps
> = ({
  index,
  name,
  description,
  grade,
  illustration,
  observations,
  plusPoints,
  variant: variantProp = "default",
  container,
  questions,
  main,
  lockPrompts = false,
  loading,
  loadingObservations,
  onPrompt,
  onObservationsClick,
  noData,
}) => {
  const id = `analysisSection${index}`;

  const ref = useContentMenuSection<HTMLDivElement>({
    id: id,
    label: name,
    index,
  });

  const variant = (() => {
    if (variantProp === "pentaptych" && !main) {
      return "default";
    }
    return variantProp;
  })();

  const observationLength = (() => {
    if (variant === "pentaptych") {
      return 3 + (main ? 0 : 1);
    }
    return 2;
  })();

  const handleAllObservationsClick = () => {
    onObservationsClick(index);
  };

  const [customPrompt, setCustomPrompt] = useState<string>("");

  const handleObservationClick = (observation: AnalysisObservationType) => {
    onObservationsClick(index, observation);
  };

  const handlePromptClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!customPrompt) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onPrompt(customPrompt);
    setCustomPrompt("");
  };

  const handlePromptKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && customPrompt) {
      e.preventDefault();
      e.stopPropagation();
      onPrompt(customPrompt);
      setCustomPrompt("");
    }
  };

  return (
    <Section id={id} ref={ref}>
      <Container size={container}>
        {index !== undefined && (
          <AnalysisIndicator index={index} illustration={illustration} />
        )}
        <Heading
          title={name}
          variant="main"
          description={loading || noData ? <SummaryLoading /> : description}
        />

        {!!grade && <GaugeCard name={name} grade={grade} loading={loading} />}

        {loading && (
          <>
            {variant === "pentaptych" && (
              <Pentaptych>
                {main}
                {Array.from({ length: observationLength }).map((_, i) => (
                  <AnalysisObservation
                    key={i}
                    loading
                    title={loadingObservations?.[i]}
                  />
                ))}
                <AllAnalysisObservations loading />
              </Pentaptych>
            )}

            {variant === "default" && (
              <Grid variant="threeColumn">
                {Array.from({ length: observationLength }).map((_, i) => (
                  <AnalysisObservation
                    key={i}
                    loading
                    title={loadingObservations?.[i]}
                  />
                ))}
                <AllAnalysisObservations loading />
              </Grid>
            )}
          </>
        )}

        {!loading && !!observations && observations?.length > 0 && (
          <>
            {variant === "pentaptych" && (
              <Pentaptych>
                {main}
                {observations
                  ?.slice(0, observationLength)
                  .map((observation, i) => (
                    <AnalysisObservation
                      key={i}
                      observation={observation}
                      onClick={() => handleObservationClick(observation)}
                    />
                  ))}
                <AllAnalysisObservations
                  length={observations?.length ?? 0}
                  onClick={handleAllObservationsClick}
                />
              </Pentaptych>
            )}

            {variant === "default" && (
              <Grid variant="threeColumn">
                {observations
                  ?.slice(0, observationLength)
                  .map((observation, i) => (
                    <AnalysisObservation
                      key={i}
                      observation={observation}
                      onClick={() => handleObservationClick(observation)}
                    />
                  ))}
                <AllAnalysisObservations
                  length={observations?.length ?? 0}
                  onClick={handleAllObservationsClick}
                />
              </Grid>
            )}
          </>
        )}

        {!loading && !!plusPoints && plusPoints?.length > 0 && (
          <>
            <Spacing height="xs" />
            <Grid variant="twoColumn">
              <Flex>
                <Text variant="displayL" className={plusHeadingStyle} bold>
                  <GradientText>Pluspunten</GradientText> {name.toLowerCase()}
                </Text>
              </Flex>

              <Flex
                as="ul"
                direction="column"
                className={mergeClasses(
                  plusPointsListStyle,
                  plusPointsOffsetStyle,
                )}
              >
                <Separate>
                  {plusPoints.map((plusPoint, i) => (
                    <Flex
                      as="li"
                      key={`${i}-${plusPoint.label}`}
                      align="center"
                      gap="s"
                    >
                      <Icon icon="checkCircleFilled" />
                      <Text grow>{plusPoint.label}</Text>
                    </Flex>
                  ))}
                </Separate>
              </Flex>
            </Grid>
          </>
        )}

        {!loading && !!questions && questions?.length > 0 && (
          <>
            <Spacing height="xs" />
            <Card variant="tertiary">
              <Flex>
                <Text variant="displayM" bold>
                  Vragen over {name.toLowerCase()}
                </Text>
              </Flex>
              <Flex gap="xs" wrap>
                {questions.map((question, i) => (
                  <Suggestion
                    key={i}
                    suggestion={question}
                    onClick={(question) => onPrompt(question)}
                  />
                ))}
              </Flex>
              <TextField
                placeholder={`Vraag mij alles over ${name.toLowerCase()}`}
                leading={<Icon icon="aiFilled" color="gradient" />}
                value={customPrompt}
                onChange={setCustomPrompt}
                onKeyDown={handlePromptKeyDown}
                disabled={lockPrompts}
                trailing={
                  <div
                    style={{
                      opacity: `${!customPrompt || lockPrompts ? 0 : 1}`,
                    }}
                  >
                    <IconButton
                      variant="opaque"
                      disabled={!customPrompt || lockPrompts}
                      size="xs"
                      icon="arrowRight"
                      onClick={handlePromptClick}
                      rounded
                    />
                  </div>
                }
              />
            </Card>
          </>
        )}
      </Container>

      <Spacing />

      <Text align="center">
        {padNumber(index)} &ndash; {name}
      </Text>
    </Section>
  );
};
